import {createContext, useContext, useMemo, useRef, useState, type PropsWithChildren} from 'react';
import noop from '@tinkoff/utils/function/noop';
import {useEventCallback} from 'usehooks-ts';

type Trigger = 'caret' | 'mouse' | 'toolbar';

type PreviewEvent =
    'mouseover' | 'mouseout' | 'caretin' | 'caretout' | 'overlayclosed' |
    'escape' | 'edit' | 'close' | 'disable' | 'enable' | 'toolbarclicked';

type PreviewState = 'opened' | 'closed' | 'hidden' | 'disabled';

type PreviewMode = 'preview' | 'editing';

type LinkPreviewContext = {
    isOpened: boolean;
    isEditing: boolean;
    trigger: Trigger | undefined;
    triggerNode: HTMLElement | null | undefined;
    handleLinkPreviewEvent: (event: PreviewEvent, node?: HTMLElement | null, delay?: number) => void | boolean;
};

const LinkPreviewContext = createContext<LinkPreviewContext>({
    isOpened: false,
    isEditing: false,
    trigger: undefined,
    triggerNode: undefined,
    handleLinkPreviewEvent: noop,
});

export const LinkPreviewContextProvider = ({children}: PropsWithChildren) => {
    const [trigger, setTrigger] = useState<Trigger>();
    const [triggerNode, setTriggerNode] = useState<HTMLElement | null>();
    const [previewState, setPreviewState] = useState<PreviewState>('closed');
    const [previewMode, setPreviewMode] = useState<PreviewMode>('preview');

    const delayTimoutId = useRef<ReturnType<typeof setTimeout>>();

    const isOpenedByCaret = previewState === 'opened' && trigger === 'caret';
    const isOpenedInEditMode = previewState === 'opened' && previewMode === 'editing';

    const handleLinkPreviewEventImpl = (
        event: PreviewEvent,
        node?: HTMLElement | null,
    ) => {
        if (previewState === 'disabled' && event !== 'enable' && event !== 'toolbarclicked') {
            return false;
        }

        switch (event) {
        case 'disable':
            setPreviewState('disabled');
            setTriggerNode(undefined);
            setTrigger(undefined);
            setPreviewMode('preview');
            return true;
        case 'enable':
            if (previewState === 'disabled') {
                setPreviewState('closed');
                return true;
            }

            return false;
        case 'mouseover':
            if (isOpenedInEditMode || isOpenedByCaret) {
                return false;
            }

            setPreviewState('opened');
            setTriggerNode(node);
            setTrigger('mouse');
            setPreviewMode('preview');
            return true;
        case 'mouseout':
            if (isOpenedInEditMode || isOpenedByCaret) {
                return false;
            }

            setPreviewState('closed');
            setTriggerNode(undefined);
            setTrigger(undefined);
            setPreviewMode('preview');

            return true;
        case 'caretin':
            if (previewState === 'hidden' && node === triggerNode) {
                return false;
            }

            setPreviewState('opened');
            setPreviewMode('preview');
            setTriggerNode(node);
            setTrigger('caret');
            return true;
        case 'caretout':
        case 'close':
            setPreviewState('closed');
            setTriggerNode(undefined);
            setTrigger(undefined);
            return true;
        case 'overlayclosed':
            if (isOpenedByCaret) {
                setPreviewState('hidden');
                return true;
            }

            setPreviewState('closed');
            setTriggerNode(undefined);
            setTrigger(undefined);
            return true;
        case 'escape':
            if (isOpenedByCaret) {
                setPreviewState('hidden');
                return true;
            }

            return false;
        case 'edit':
            setPreviewMode('editing');
            return true;
        case 'toolbarclicked':
            setPreviewState('opened');
            setPreviewMode('editing');
            setTriggerNode(node);
            setTrigger('toolbar');
            return true;
        }
    };
    const handleLinkPreviewEvent = useEventCallback((
        event: PreviewEvent,
        node?: HTMLElement | null,
        delay?: number,
    ) => {
        clearTimeout(delayTimoutId.current);

        if (!delay) {
            handleLinkPreviewEventImpl(event, node);
            return;
        }

        delayTimoutId.current = setTimeout(() => handleLinkPreviewEventImpl(event, node), delay);
    });

    const value = useMemo(() => ({
        handleLinkPreviewEvent,
        isOpened: previewState === 'opened',
        isEditing: previewMode === 'editing',
        triggerNode,
        trigger,
    }), [handleLinkPreviewEvent, previewState, previewMode, triggerNode, trigger]);

    return (
        <LinkPreviewContext.Provider value={value}>
            {children}
        </LinkPreviewContext.Provider>
    );
};

export const useLinkPreviewContext = () => {
    return useContext(LinkPreviewContext);
};
